/**
 * Intelligence Report Theme Colors
 *
 * Provides a custom color palette for the intelligence report:
 * - Light mode: Uses standard MUI theme colors
 * - Dark mode: Uses zinc/lime palette from the elegant design (zinc-950, lime-400)
 */

export const zincColors = {
  50: '#fafafa',
  100: '#f4f4f5',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#71717a',
  600: '#52525b',
  700: '#3f3f46',
  800: '#27272a',
  900: '#18181b',
  950: '#09090b',
};

export const limeColors = {
  400: '#a3e635',
  500: '#84cc16',
};

export const amberColors = {
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b',
  600: '#d97706',
};

/**
 * Get intelligence-specific colors based on theme mode
 */
export const getIntelligenceColors = (isDark: boolean) => {
  if (isDark) {
    return {
      background: {
        primary: zincColors[950],
        secondary: zincColors[900],
        tertiary: `${zincColors[900]}99`, // 60% opacity
        paper: `${zincColors[900]}66`, // 40% opacity
      },
      border: {
        primary: zincColors[800],
        secondary: zincColors[700],
      },
      text: {
        primary: zincColors[100],
        secondary: zincColors[200],
        tertiary: zincColors[300],
        muted: zincColors[400],
        dim: zincColors[500],
      },
      accent: {
        primary: limeColors[400],
        secondary: limeColors[500],
      },
      risk: {
        background: `${amberColors[500]}1A`, // 10% opacity
        border: `${amberColors[600]}66`, // 40% opacity
        text: amberColors[300],
        dot: amberColors[400],
      },
    };
  } else {
    // Light mode - return null to use standard theme
    return null;
  }
};

/**
 * Get meter/progress bar color
 * In dark mode: uses lime-400
 * In light mode: uses gradient from red → yellow → green
 */
export const getMeterColor = (score: number, isDark: boolean): string => {
  if (isDark) {
    return limeColors[400];
  }

  // Light mode: gradient based on score
  const clampedScore = Math.max(0, Math.min(100, score));

  if (clampedScore <= 50) {
    // Red to Yellow transition (0-50)
    const ratio = clampedScore / 50;
    const red = 255;
    const green = Math.round(ratio * 255);
    const blue = 0;
    return `rgb(${red}, ${green}, ${blue})`;
  } else {
    // Yellow to Green transition (50-100)
    const ratio = (clampedScore - 50) / 50;
    const red = Math.round(255 * (1 - ratio));
    const green = 255;
    const blue = 0;
    return `rgb(${red}, ${green}, ${blue})`;
  }
};

/**
 * Get grade pill colors based on score and theme mode
 */
export const getGradePillColors = (score: number, isDark: boolean) => {
  let label = 'Moderate';
  let color = '';
  let backgroundColor = '';
  let borderColor = '';

  if (score >= 85) {
    label = 'Excellent';
  } else if (score >= 70) {
    label = 'High';
  } else if (score >= 50) {
    label = 'Medium';
  }

  if (isDark) {
    // Zinc palette for dark mode
    color = zincColors[100];
    backgroundColor = zincColors[800];
    borderColor = zincColors[700];
  } else {
    // Light mode - colored pills
    if (score >= 85) {
      color = '#10b981'; // emerald-500
      backgroundColor = 'rgba(16, 185, 129, 0.15)';
      borderColor = 'rgba(16, 185, 129, 0.3)';
    } else if (score >= 70) {
      color = '#3b82f6'; // blue-500
      backgroundColor = 'rgba(59, 130, 246, 0.15)';
      borderColor = 'rgba(59, 130, 246, 0.3)';
    } else if (score >= 50) {
      color = '#f59e0b'; // amber-500
      backgroundColor = 'rgba(245, 158, 11, 0.15)';
      borderColor = 'rgba(245, 158, 11, 0.3)';
    } else {
      color = '#ef4444'; // red-500
      backgroundColor = 'rgba(239, 68, 68, 0.15)';
      borderColor = 'rgba(239, 68, 68, 0.3)';
    }
  }

  return {
    label,
    color,
    backgroundColor,
    borderColor,
  };
};

/**
 * Get insight chip colors (for quick insights)
 */
export const getInsightChipColors = (isDark: boolean) => {
  if (isDark) {
    return {
      background: `${zincColors[900]}80`, // 50% opacity
      border: zincColors[800],
      dotColor: limeColors[400],
      textPrimary: zincColors[100],
      textSecondary: zincColors[400],
    };
  } else {
    return null; // Use theme defaults
  }
};